import { Card, Rate, Space, Tag } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import tw from "tailwind-styled-components";
import PageTitle from "../page-header/PageHeader";
import Loader from "../loader/loader";

const DetailPerformanceFeedback = () => {
	const { id } = useParams();
	const [feedback, setFeedback] = useState(null);
	const [loading, setLoading] = useState(true);
	const isLogged = Boolean(localStorage.getItem("isLogged"));

	useEffect(() => {
		// Get feedback from localStorage
		const storedFeedback = localStorage.getItem("performanceFeedback");
		if (storedFeedback) {
			try {
				const feedbackList = JSON.parse(storedFeedback);
				const feedbackId = parseInt(id);
				const foundFeedback = feedbackList.find((f) => f.id === feedbackId || String(f.id) === String(id));
				if (foundFeedback) {
					setFeedback(foundFeedback);
				}
			} catch (error) {
				console.error("Error parsing feedback from localStorage:", error);
			}
		}
		setLoading(false);
	}, [id]);

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	if (loading) {
		return <Loader />;
	}

	if (!feedback) {
		return (
			<div>
				<PageTitle title="Back" />
				<Card className="mt-4">
					<div className="text-center">
						<h3 className="text-xl text-red-500">Performance Feedback not found</h3>
						<p className="text-gray-600 mt-2">The feedback with ID {id} could not be found.</p>
					</div>
				</Card>
			</div>
		);
	}

	const getStatusColor = (status) => {
		switch (status) {
			case "Completed":
				return "green";
			case "Draft":
				return "orange";
			default:
				return "blue";
		}
	};

	const getRatingColor = (rating) => {
		if (rating >= 4) return "green";
		if (rating >= 3) return "blue";
		if (rating >= 2) return "orange";
		return "red";
	};

	return (
		<div>
			<PageTitle title="Back" />
			<Card className="mt-4">
				<div className="text-center mb-4">
					<h2 className="text-2xl font-semibold text-gray-600">
						Performance Feedback #{feedback.id}
					</h2>
				</div>
				{feedback ? (
					<div className="flex justify-center">
						<ul className="list-inside list-none border-2 border-inherit rounded px-5 py-5">
							<ListItem>
								Employee:{" "}
								<TextInside>{feedback.employeeName || "N/A"}</TextInside>
							</ListItem>
							<ListItem>
								Reviewer:{" "}
								<TextInside>{feedback.reviewerName || "N/A"}</TextInside>
							</ListItem>
							<ListItem>
								Feedback Date:{" "}
								<TextInside>
									{feedback.feedbackDate
										? dayjs(feedback.feedbackDate).format("DD-MM-YYYY")
										: "N/A"}
								</TextInside>
							</ListItem>
							<ListItem>
								Review Period:{" "}
								<TextInside>{feedback.reviewPeriod || "N/A"}</TextInside>
							</ListItem>
							<ListItem>
								Status:{" "}
								<TextInside>
									<Tag color={getStatusColor(feedback.status)}>
										{feedback.status || "Pending"}
									</Tag>
								</TextInside>
							</ListItem>
							<ListItem>
								Overall Rating:{" "}
								<TextInside>
									<Space>
										<Rate disabled defaultValue={feedback.overallRating} allowHalf style={{ fontSize: 16 }} />
										<Tag color={getRatingColor(feedback.overallRating)}>
											{feedback.overallRating}/5
										</Tag>
									</Space>
								</TextInside>
							</ListItem>
							<ListItem>
								Technical Skills:{" "}
								<TextInside>
									<Rate disabled defaultValue={feedback.technicalSkills} allowHalf style={{ fontSize: 16 }} />
									<span className="ml-2">({feedback.technicalSkills}/5)</span>
								</TextInside>
							</ListItem>
							<ListItem>
								Communication:{" "}
								<TextInside>
									<Rate disabled defaultValue={feedback.communication} allowHalf style={{ fontSize: 16 }} />
									<span className="ml-2">({feedback.communication}/5)</span>
								</TextInside>
							</ListItem>
							<ListItem>
								Teamwork:{" "}
								<TextInside>
									<Rate disabled defaultValue={feedback.teamwork} allowHalf style={{ fontSize: 16 }} />
									<span className="ml-2">({feedback.teamwork}/5)</span>
								</TextInside>
							</ListItem>
							<ListItem>
								Problem Solving:{" "}
								<TextInside>
									<Rate disabled defaultValue={feedback.problemSolving} allowHalf style={{ fontSize: 16 }} />
									<span className="ml-2">({feedback.problemSolving}/5)</span>
								</TextInside>
							</ListItem>
							<ListItem>
								Strengths:{" "}
								<TextInside>{feedback.strengths || "N/A"}</TextInside>
							</ListItem>
							<ListItem>
								Areas for Improvement:{" "}
								<TextInside>{feedback.areasForImprovement || "N/A"}</TextInside>
							</ListItem>
							{feedback.goals && (
								<ListItem>
									Goals for Next Period:{" "}
									<TextInside>{feedback.goals}</TextInside>
								</ListItem>
							)}
							{feedback.comments && (
								<ListItem>
									Additional Comments:{" "}
									<TextInside>{feedback.comments}</TextInside>
								</ListItem>
							)}
						</ul>
					</div>
				) : (
					<Loader />
				)}
			</Card>
		</div>
	);
};

const ListItem = tw.li`
	text-sm
	text-gray-600
	font-semibold
	py-2
	px-4
	bg-gray-100
	mb-1.5
	rounded
	w-96
	flex
	justify-start
`;

const TextInside = tw.p`
	ml-2
	text-sm
	text-gray-900
`;


export default DetailPerformanceFeedback;

